import usePrivateAxios from '@/common/api/usePrivateAxios'
import { defaultTransformerResponse } from '@/common/constants/defaultTransformerResponse.constant'
import { Mapper } from '@/common/services/mapper'
import { MetaResponse } from '@/common/types/metaResponse'
import { EquipmentModelIntegrationCodeData } from '@/features/equipmentModels/types/equipmentModelIntegrationCode'
import { keepPreviousData, useQuery } from '@tanstack/react-query'

type UseEquipmentModelIntegrationCodesProps<T> = {
  equipmentModelId?: string
  filters?: {
    search?: string
    page?: number
  }
  mapper?: (data: any) => T
  enabled?: boolean
  refetchInterval?: number | false
}

export const useEquipmentModelIntegrationCodes = <T,>({
  equipmentModelId,
  filters: { search = '', page } = {},
  mapper = Mapper.default<T>(),
  enabled = true,
  refetchInterval = false,
}: UseEquipmentModelIntegrationCodesProps<T>) => {
  const privateAxios = usePrivateAxios()

  const {
    data: { data, meta } = defaultTransformerResponse<T>(),
    isPending,
    ...rest
  } = useQuery({
    queryKey: ['equipmentModelIntegrationCodes', equipmentModelId, search, page],
    enabled: enabled && !!equipmentModelId,
    queryFn: () =>
      privateAxios.get(`/equipment_models/${equipmentModelId}/integration_codes`, {
        params: {
          search: search || undefined,
          page,
        },
      }),
    select: (response: { data: { data: EquipmentModelIntegrationCodeData[]; meta: MetaResponse } }) => ({
      data: mapper(response?.data?.data),
      meta: response?.data?.meta,
    }),
    placeholderData: keepPreviousData,
    refetchInterval,
  })

  return { data, meta, isPending, ...rest }
}
